import React, { useCallback, useEffect, useMemo, useState } from "react";
import RequestIcon from "@mui/icons-material/AssignmentOutlined";
import CountryIcon from "@mui/icons-material/LanguageOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ContactedIcon from "@mui/icons-material/TaskAltOutlined";
import ClosedIcon from "@mui/icons-material/CloseOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Alert,
  Box,
  debounce,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, CustomeDataGrid, Chip as UiChip, TextField } from "../../components/ui";
import { showErrorToast, showSuccessToast } from "../../components/common/ShowToast";
import WhatsAppMessageDialog from "../../components/common/WhatsAppMessageDialog";
import apiService from "../../service/Api";
import type { RootState } from "../../store/Index";
import { tokens } from "../../theme";

export type ChecklistRequestStatus = "NEW" | "CONTACTED" | "CLOSED";

export interface DiasporaChecklistRequest {
  id: number;
  full_name: string;
  whatsapp_number: string;
  based_in: string;
  service_location: string;
  contact?: number | null;
  contact_image?: string | null;
  status: ChecklistRequestStatus;
  created_at: string;
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: DiasporaChecklistRequest[];
}

const STATUS_LABELS: Record<ChecklistRequestStatus, string> = {
  NEW: "New",
  CONTACTED: "Contacted",
  CLOSED: "Closed",
};

const STATUS_COLORS: Record<
  ChecklistRequestStatus,
  "info" | "warning" | "neutral"
> = {
  NEW: "info",
  CONTACTED: "warning",
  CLOSED: "neutral",
};

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const DiasporaChecklistRequestsPage: React.FC = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const navigate = useNavigate();
  const [requests, setRequests] = useState<DiasporaChecklistRequest[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    ChecklistRequestStatus | "ALL"
  >("ALL");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [messageRequest, setMessageRequest] = useState<DiasporaChecklistRequest | null>(null);

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 300), []);

  useEffect(() => {
    return () => debouncedSetSearch.clear();
  }, [debouncedSetSearch]);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await apiService.get<PaginatedResponse>(
        "/api/bookings/diaspora-checklist-requests/",
        {
          page: paginationModel.page + 1,
          page_size: paginationModel.pageSize,
          search: search || undefined,
          status: statusFilter === "ALL" ? undefined : statusFilter,
        },
      );
      setRequests(response.data.results ?? []);
      setRowCount(response.data.count ?? 0);
    } catch (error) {
      console.error("Failed to load diaspora checklist requests", error);
      setLoadError("Checklist requests could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, search, statusFilter]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  // Reset to the first page whenever the search or status filter changes.
  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [search, statusFilter]);

  const [statusCounts, setStatusCounts] = useState<Record<ChecklistRequestStatus, number>>({
    NEW: 0,
    CONTACTED: 0,
    CLOSED: 0,
  });

  // Status counts for the summary cards are independent of the current
  // page/search/status filter, so they're fetched separately (page_size=1
  // keeps the payload tiny -- only the `count` field is used).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [newRes, contactedRes, closedRes] = await Promise.all(
          (["NEW", "CONTACTED", "CLOSED"] as ChecklistRequestStatus[]).map((status) =>
            apiService.get<PaginatedResponse>(
              "/api/bookings/diaspora-checklist-requests/",
              { status, page_size: 1 },
            ),
          ),
        );
        if (cancelled) return;
        setStatusCounts({
          NEW: newRes.data.count ?? 0,
          CONTACTED: contactedRes.data.count ?? 0,
          CLOSED: closedRes.data.count ?? 0,
        });
      } catch (error) {
        console.error("Failed to load diaspora checklist status counts", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requests]);

  const updateStatus = async (
    request: DiasporaChecklistRequest,
    status: ChecklistRequestStatus,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    try {
      await apiService.patch(
        `/api/bookings/diaspora-checklist-requests/${request.id}/`,
        { status },
      );
      setRequests((current) =>
        current.map((item) =>
          item.id === request.id ? { ...item, status } : item,
        ),
      );
      showSuccessToast(`Request marked ${STATUS_LABELS[status].toLowerCase()}.`);
    } catch {
      showErrorToast("Could not update the request status.");
    }
  };

  const openWhatsAppDialog = (
    request: DiasporaChecklistRequest,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setMessageRequest(request);
  };

  const columns: GridColDef<DiasporaChecklistRequest>[] = [
    {
      field: "full_name",
      headerName: "APPLICANT",
      minWidth: 200,
      flex: 1.1,
      renderCell: (params: GridRenderCellParams<DiasporaChecklistRequest>) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {params.row.full_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.whatsapp_number}
          </Typography>
        </Box>
      ),
    },
    {
      field: "based_in",
      headerName: "BASED IN",
      minWidth: 150,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams<DiasporaChecklistRequest>) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <CountryIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="body2">{params.row.based_in}</Typography>
        </Stack>
      ),
    },
    {
      field: "service_location",
      headerName: "SERVICE LOCATION",
      minWidth: 190,
      flex: 1,
    },
    {
      field: "created_at",
      headerName: "REQUESTED",
      minWidth: 155,
      flex: 0.9,
      valueGetter: (_value, row) => formatDate(row.created_at),
    },
    {
      field: "status",
      headerName: "STATUS",
      minWidth: 120,
      flex: 0.65,
      renderCell: (params: GridRenderCellParams<DiasporaChecklistRequest>) => (
        <UiChip
          label={STATUS_LABELS[params.row.status]}
          status={STATUS_COLORS[params.row.status]}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 104,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<DiasporaChecklistRequest>) => (
        <Stack direction="row" spacing={0.25}>
          <Box
            component="button"
            type="button"
            aria-label={`Message ${params.row.full_name} on WhatsApp`}
            title="Message on WhatsApp"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
              openWhatsAppDialog(params.row, event)
            }
            sx={{
              border: 0,
              bgcolor: "transparent",
              color: colors.primary[400],
              p: 0.75,
              cursor: "pointer",
              borderRadius: 1,
              "&:hover": { bgcolor: alpha(colors.primary[400], 0.08) },
            }}
          >
            <WhatsAppIcon fontSize="small" />
          </Box>
          {params.row.status === "NEW" && (
            <Box
              component="button"
              type="button"
              aria-label={`Mark ${params.row.full_name} as contacted`}
              title="Mark contacted"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                updateStatus(params.row, "CONTACTED", event)
              }
              sx={{
                border: 0,
                bgcolor: "transparent",
                color: colors.primary[400],
                p: 0.75,
                cursor: "pointer",
                borderRadius: 1,
                "&:hover": { bgcolor: alpha(colors.primary[400], 0.08) },
              }}
            >
              <ContactedIcon fontSize="small" />
            </Box>
          )}
        </Stack>
      ),
    },
  ];

  const { NEW: newCount, CONTACTED: contactedCount, CLOSED: closedCount } = statusCounts;

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color={colors.primary[100]}>
            Diaspora Checklist Requests
          </Typography>
          <Typography color="text.secondary" mt={0.5} variant="body2">
            Review checklist requests and follow up with families planning from
            abroad.
          </Typography>
        </Box>
        <UiChip
          icon={<RequestIcon />}
          label={`${rowCount} requests`}
          status="neutral"
        />
      </Stack>

      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard
            label="New requests"
            value={newCount}
            icon={<RequestIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard
            label="Contacted"
            value={contactedCount}
            icon={<ContactedIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard
            label="Closed"
            value={closedCount}
            icon={<ClosedIcon />}
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "14px",
          boxShadow: 0,
          bgcolor: colors.primary[900],
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2.5}>
          <TextField
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              debouncedSetSearch(event.target.value);
            }}
            placeholder="Search name, WhatsApp, or location"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 190 } }}>
            <InputLabel id="diaspora-checklist-status-label">Status</InputLabel>
            <Select
              labelId="diaspora-checklist-status-label"
              value={statusFilter}
              label="Status"
              onChange={(event) =>
                setStatusFilter(
                  event.target.value as ChecklistRequestStatus | "ALL",
                )
              }
            >
              <MenuItem value="ALL">All statuses</MenuItem>
              <MenuItem value="NEW">New</MenuItem>
              <MenuItem value="CONTACTED">Contacted</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void loadRequests()}
              >
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}
        <CustomeDataGrid
          rows={requests}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          onRowClick={(params) =>
            navigate(`/diaspora-checklist-requests/${params.row.id}`, {
              state: { request: params.row as DiasporaChecklistRequest },
            })
          }
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowHeight={() => "auto"}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": {
              borderBottomColor: alpha(theme.palette.text.primary, 0.04),
            },
            "& .MuiDataGrid-columnHeaders": { borderBottom: "none" },
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
          localeText={{
            noRowsLabel: loading
              ? "Loading checklist requests…"
              : "No checklist requests found.",
          }}
        />
      </Paper>
      <WhatsAppMessageDialog
        open={!!messageRequest}
        onClose={() => setMessageRequest(null)}
        clientName={messageRequest?.full_name ?? ""}
        clientPhone={messageRequest?.whatsapp_number ?? ""}
        staffName={user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "Muzukuru"}
        topic={`your request for the diaspora funeral planning checklist for ${messageRequest?.service_location ?? ""}`}
        initialMessage={messageRequest ? `Hi ${messageRequest.full_name.trim().split(/\s+/)[0]}, thanks for requesting the Muzukuru diaspora funeral planning checklist. Is there anything we can help you coordinate for the service near ${messageRequest.service_location}?` : ""}
        templateStorageKey="whatsapp_diaspora_checklist_template"
      />
    </Box>
  );
};

const SummaryCard = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        minHeight: 88,
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: "14px",
        boxShadow: 0,
        bgcolor: colors.primary[900],
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          bgcolor: `${colors.primary[400]}20`,
          color: colors.primary[400],
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

export default DiasporaChecklistRequestsPage;
